import websocketService from '../websocket';
import { createWebSocketRequest } from './utils';

/**
 * Subscribe to real-time price updates for instruments
 * @param {Array} instruments - Array of instruments to subscribe to, each with id, type, and symbol
 * @returns {Promise<Object>} A promise that resolves with the subscription response
 */
export const subscribeToInstrumentPrices = (instruments) => {
  // Check basic conditions
  if (!instruments || !Array.isArray(instruments) || instruments.length === 0) {
    console.error('Invalid instruments array provided for subscription');
    return Promise.resolve({
      success: true,
      subscribed: [],
      failed: []
    });
  }

  // Check WebSocket connection first
  if (websocketService.getConnectionState() !== 'connected') {
    console.error('WebSocket is not connected. Cannot subscribe to instrument prices.');
    // Return a fake success response to avoid blocking the UI
    return Promise.resolve({
      success: true,
      subscribed: [],
      failed: instruments.map(instr => ({
        ...instr,
        reason: 'WebSocket not connected'
      }))
    });
  }
  
  // Check if authenticated
  if (!websocketService.isAuthenticated()) {
    console.error('WebSocket is not authenticated. Cannot subscribe to instrument prices.');
    // Return a fake success response to avoid blocking the UI
    return Promise.resolve({
      success: true,
      subscribed: [],
      failed: instruments.map(instr => ({
        ...instr,
        reason: 'WebSocket not authenticated'
      }))
    });
  }

  // Format instruments according to API requirements
  const formattedInstruments = instruments.map(instrument => {
    // Get the full ID from the instrument
    const fullId = instrument.id || instrument.instrumentId || instrument.symbol;
    
    if (!fullId) {
      return null;
    }
    
    // Parse the ID to extract components (format: "ID.TYPE.SYMBOL")
    let id, type, symbol;
    
    if (typeof fullId === 'string' && fullId.includes('.')) {
      // Split the ID by dots
      const parts = fullId.split('.');
      
      if (parts.length >= 3) {
        // For format like "0000004.FX.AUDNZD"
        id = parts[0]; // e.g., "0000004"
        type = parts[1]; // e.g., "FX"
        symbol = parts.slice(2).join('.'); // e.g., "AUDNZD" (join remaining parts in case symbol has dots)
      } else if (parts.length === 2) {
        // For format like "STOCK.AAPL"
        id = fullId;
        type = parts[0]; // e.g., "STOCK"
        symbol = parts[1]; // e.g., "AAPL"
      } else {
        // Fallback for unexpected format
        id = fullId;
        type = instrument.type || 'Unknown';
        symbol = fullId;
      }
    } else {
      // If the ID doesn't have dots, use provided fields or defaults
      id = fullId;
      
      // Format the type, converting "Forex" to "FX" internally
      type = instrument.type || 'FX';
      if (typeof type === 'string') {
        type = type.charAt(0).toUpperCase() + type.slice(1).toLowerCase();
        // Convert "Forex" to "FX" as required by backend
        if (type === 'Forex') {
          type = 'FX';
        }
      }
      
      // Get the symbol (default to the ID if not provided)
      symbol = instrument.symbol || fullId;
    }
    
    return { id, type, symbol };
  }).filter(Boolean); // Remove any null entries

  // Check if we have any valid instruments after formatting
  if (formattedInstruments.length === 0) {
    console.error('No valid instruments after formatting for subscription');
    return Promise.resolve({
      success: true,
      subscribed: [],
      failed: instruments.map(instr => ({
        ...instr,
        reason: 'Invalid instrument format'
      }))
    });
  }

  // Now use a custom createWebSocketRequest since this API response is structured differently
  return new Promise((resolve) => {
    // Event name for the response
    const eventName = 'subscribe-instrument-ack';
    
    // Create one-time event listener for the response
    const responseHandler = (data) => {
      // Remove the listener after receiving the response
      websocketService.off(eventName, responseHandler);
      
      // Clear the timeout as we've received a response
      clearTimeout(timeoutId);
      
      // Process the response
      if (!data) {
        console.error('Empty response for price subscription request');
        return resolve({
          success: true,
          subscribed: [],
          failed: formattedInstruments.map(instr => ({
            ...instr,
            reason: 'Empty response from server'
          }))
        });
      }
      
      try {
        // Check for success in the new response format
        if (data.data && typeof data.data.success === 'boolean') {
          const responseData = data.data;
          
          if (responseData.success) {
            // Successfully subscribed to at least some instruments
            // Check for failed subscriptions
            if (responseData.failed && responseData.failed.length > 0) {
              console.warn(`Failed to subscribe to ${responseData.failed.length} instruments`);
            }
            
            // Resolve with full response data for handling by caller
            resolve({
              success: true,
              subscribed: responseData.subscribed || [],
              failed: responseData.failed || []
            });
          } else {
            // All subscriptions failed
            const failedCount = responseData.failed?.length || 0;
            const errorMessage = `Failed to subscribe to ${failedCount} instruments`;
            console.error(errorMessage);
            resolve({
              success: true,
              subscribed: [],
              failed: responseData.failed || formattedInstruments.map(instr => ({
                ...instr,
                reason: 'Server reported failure'
              }))
            });
          }
        } else {
          // Fallback for unexpected response format
          if (data.success === true || 
              (data.data && data.data.success === true) ||
              data.status === 'success') {
            resolve({
              success: true,
              subscribed: formattedInstruments,
              failed: []
            });
          } else {
            const errorMessage = data.message || data.error || 'Failed to subscribe to instrument prices';
            console.error(errorMessage);
            resolve({
              success: true,
              subscribed: [],
              failed: formattedInstruments.map(instr => ({
                ...instr,
                reason: errorMessage
              }))
            });
          }
        }
      } catch (error) {
        console.error('Error processing price subscription response:', error);
        resolve({
          success: true,
          subscribed: [],
          failed: formattedInstruments.map(instr => ({
            ...instr,
            reason: `Error processing response: ${error.message}`
          }))
        });
      }
    };
    
    // Set up the event listener for the response
    websocketService.on(eventName, responseHandler);
    
    // Set a timeout to prevent hanging if no response is received
    const timeoutId = setTimeout(() => {
      websocketService.off(eventName, responseHandler);
      console.warn('Timeout waiting for price subscription response');
      resolve({
        success: true,
        subscribed: [], // No instruments were actually subscribed
        failed: formattedInstruments.map(instr => ({
          ...instr,
          reason: 'Timeout waiting for response'
        }))
      });
    }, 30000); // 30 second timeout
    
    try {
      // Send the subscription request
      websocketService.send({
        type: "subscribe-instrument",
        data: formattedInstruments
      });
    } catch (error) {
      // Clean up in case of error
      clearTimeout(timeoutId);
      websocketService.off(eventName, responseHandler);
      console.error('Error sending subscription request:', error);
      resolve({
        success: true,
        subscribed: [],
        failed: formattedInstruments.map(instr => ({
          ...instr,
          reason: `Error sending request: ${error.message}`
        }))
      });
    }
  });
};

/**
 * Unsubscribe from real-time price updates for instruments
 * @param {Array} instruments - Array of instrument objects or IDs to unsubscribe from
 * @returns {Promise<Object>} A promise that resolves with the unsubscription response
 */
export const unsubscribeFromInstrumentPrices = (instruments) => {
  // Validate parameter
  if (!instruments || !Array.isArray(instruments) || instruments.length === 0) {
    return Promise.reject(new Error('Invalid instruments array provided for unsubscription'));
  }
  
  // Format instrument IDs for the API - convert to strings in format "id.type.symbol"
  const instrumentStrings = instruments.map(instrument => {
    if (!instrument) {
      return null;
    }
    
    // Check if it's already a string
    if (typeof instrument === 'string') {
      return instrument;
    }
    
    // Check if it's an object with id, type, and symbol
    if (typeof instrument === 'object') {
      if (instrument.id && instrument.type && instrument.symbol) {
        return `${instrument.id}.${instrument.type}.${instrument.symbol}`;
      } else if (instrument.originalId) {
        // Handle case where we have originalId from UI
        return instrument.originalId;
      }
    }
    
    console.warn('Invalid instrument format for unsubscription:', instrument);
    return null;
  }).filter(Boolean); // Remove any null entries
  
  // Check if we have any valid instruments after formatting
  if (instrumentStrings.length === 0) {
    return Promise.reject(new Error('No valid instrument IDs after formatting for unsubscription'));
  }

  // Send a simpler format for unsubscription as requested
  return new Promise((resolve, reject) => {
    try {
      // Send the unsubscription request directly
      websocketService.send({
        type: "unsubscribe-instrument",
        data: instrumentStrings
      });
      
      // Since there might not always be a response for unsubscribe, resolve immediately
      resolve({
        success: true,
        message: `Unsubscribe request sent for ${instrumentStrings.length} instruments`
      });
    } catch (error) {
      console.error('Error sending unsubscription request:', error);
      reject(error);
    }
  });
};

/**
 * Get 24-hour price change data for an instrument
 * @param {string} symbol - The instrument symbol
 * @returns {Promise<Object>} A promise that resolves with price change data
 */
export const get24HourPriceChange = (symbol) => {
  // Validate parameter
  if (!symbol || typeof symbol !== 'string') {
    return Promise.reject(new Error('Invalid symbol provided for price change data'));
  }

  return createWebSocketRequest({
    eventName: 'price-change-24h',
    requestType: 'get-price-change-24h',
    requestData: {
      symbol: symbol
    },
    timeout: 15000, // Longer timeout for price data
    dataValidator: (data) => {
      if (data.data) {
        return data.data;
      }
      return undefined;
    },
    errorMessage: 'Failed to get 24-hour price change data: timeout'
  });
}; 